import { useEffect, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { mergeClass } from "../utils";
import _ from "lodash";
import { IPublishState } from "../components/Publishing/atoms";

const navlinkTextClass = "transition py-3 px-10 font-bold hover:bg-[#444444]";

const NavbarStaticText = ({ children }: { children: React.ReactNode }) => (
  <div className="py-2 px-10 text-[#C9C9C9] text-xs">{children}</div>
);

interface INavbarLinkProps {
  to: string;
  children?: string | React.ReactNode;
  className?: string;
}

const NavbarLink = ({ to, children, className }: INavbarLinkProps) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      mergeClass(
        navlinkTextClass,
        {
          "text-black bg-[#eeede8]": isActive,
        },
        className
      )
    }
  >
    {children}
  </NavLink>
);

const LogoutButton = () => {
  return (
    <NavLink to="/logout" className={"w-full"}>
      <button
        className={mergeClass(
          `${navlinkTextClass} text-center pb-6 pt-3 font-medium w-full`
        )}
      >
        <i className="icon-logout text-xs" /> Log Out
      </button>
    </NavLink>
  );
};

// TODO: Navbar dropdown
const NavbarDropdown = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);

  useEffect(() => {
    const ht = document
      .getElementById(`${label}_dropdown`)
      ?.getBoundingClientRect().height;
    setMenuHeight(ht || 180);
  }, []);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={mergeClass(navlinkTextClass, "flex justify-between", {
          "bg-white/10": isOpen,
        })}
      >
        <div>{label}</div>
        <i
          className={mergeClass(
            "transition icon-back_arrow -rotate-90 place-self-center",
            { "rotate-90": isOpen }
          )}
        />
      </button>
      <div
        className={mergeClass(
          "transition-height bg-white/20 overflow-auto origin-top min-h-fit"
        )}
        style={
          isOpen
            ? {
                height: `${menuHeight}px`,
              }
            : { height: "0px" }
        }
      >
        <div id={`${label}_dropdown`} className="flex flex-col min-h-fit">
          {children}
        </div>
      </div>
    </>
  );
};

const PublishTooltip = ({ children }: { children: React.ReactNode }) => (
  <div className="relative w-0 h-0 pointer-events-none">
    <div
      className={mergeClass(
        "transition absolute left-[0.4rem] bottom-[calc(100%+0.6rem)] px-2 py-0.5 rounded-tl rounded-tr rounded-br",
        "text-black font-normal text-[0.7rem] text-left opacity-0 whitespace-nowrap group-hover:opacity-100 bg-gray-200"
      )}
    >
      {children}
    </div>
  </div>
);

const PublishIndicator = ({
  children,
  color,
}: {
  children: React.ReactNode;
  color: "red" | "orange" | "none";
}) => {
  if (color === "none") return null;
  return (
    <div
      className={mergeClass(
        "transition h-2 w-2 rounded-full group group-hover",
        "absolute right-[calc(100%+0.5rem)] inset-y-[calc(50%-0.25rem)] hover:ring",
        {
          "bg-warningRed": color === "red",
          "bg-warningOrange": color === "orange",
        }
      )}
    >
      {children}
    </div>
  );
};

const NavbarTextWithIndicator = ({
  publishObj,
  children,
  className,
}: {
  children: string | React.ReactNode;
  publishObj?: IPublishState;
  className?: string;
}) => (
  <div className={mergeClass("relative", className)}>
    {!publishObj && (
      <PublishIndicator color="red">
        <PublishTooltip>Error retrieving publish state</PublishTooltip>
      </PublishIndicator>
    )}
    {publishObj && publishObj?.lastchange !== publishObj?.lastpublish && (
      <PublishIndicator color="orange">
        <PublishTooltip>Unpublished changes</PublishTooltip>
      </PublishIndicator>
    )}
    {children}
  </div>
);

export {
  NavbarLink,
  NavbarDropdown,
  NavbarStaticText,
  LogoutButton,
  NavbarTextWithIndicator,
};
